import React, { useState, useEffect, useRef } from "react";
import cn from "classnames";
import styles from "./style.scss";

interface Props {
  title: string;
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  handleClickOutside?: (ev: MouseEvent) => boolean | void;
  handleChangeWidth?: (width: number) => void;
  className?: string;
  position?: "sticky" | "fixed";
  top?: number;
  resizable?: boolean;
  width?: number;
  defaultWidth?: number;
  minWidth?: number;
  maxWidth?: number;
}

// デフォルトの幅
const DEFAULT_WIDTH = 436;
const DEFAULT_MIN_WIDTH = 320;
const DEFAULT_MAX_WIDTH = 720;

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

export const SideHelp: React.FC<Props> = ({
  title,
  isOpen,
  onClose,
  children,
  handleClickOutside,
  handleChangeWidth,
  position = "fixed",
  top = 0,
  resizable = false,
  width: controlledWidth,
  defaultWidth = DEFAULT_WIDTH,
  minWidth = DEFAULT_MIN_WIDTH,
  maxWidth = DEFAULT_MAX_WIDTH
}) => {
  const [closing, setClosing] = useState(false);
  const safeMinWidth = Math.min(minWidth, maxWidth);
  const safeMaxWidth = Math.max(minWidth, maxWidth);
  const [width, setWidth] = useState(() =>
    clamp(defaultWidth, safeMinWidth, safeMaxWidth)
  );
  const isResizingRef = useRef(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const nextWidth = clamp(width, safeMinWidth, safeMaxWidth);
    if (nextWidth === width) return;
    setWidth(nextWidth);
    handleChangeWidth?.(nextWidth);
  }, [handleChangeWidth, safeMaxWidth, safeMinWidth, width]);

  useEffect(() => {
    if (typeof controlledWidth !== "number") return;

    const nextWidth = clamp(controlledWidth, safeMinWidth, safeMaxWidth);
    if (nextWidth === width) return;

    setWidth(nextWidth);
  }, [controlledWidth, safeMaxWidth, safeMinWidth, width]);

  useEffect(() => {
    const onClickOutside = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (!isOpen) return;
      const wrapperNode = (wrapperRef.current as unknown) as Node | null;
      if (wrapperNode && target && !wrapperNode.contains(target)) {
        // 外部コールバックが存在し、明示的に false を返す場合、デフォルトのシャットダウンを防止します
        if (handleClickOutside?.(event) === false) return;
        setClosing(true);
      }
    };

    window.addEventListener("click", onClickOutside, true);
    return () => window.removeEventListener("click", onClickOutside, true);
  }, [isOpen, handleClickOutside]);

  // ドラッグ中のリサイズ処理
  const onResizePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!resizable) return;
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    event.preventDefault();
    isResizingRef.current = true;

    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const onResizePointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!isResizingRef.current) return;
    isResizingRef.current = false;
    event.currentTarget.releasePointerCapture(event.pointerId);
  };

  const onResizePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!isResizingRef.current) return;
    const width = clamp(
      window.innerWidth - event.clientX,
      safeMinWidth,
      safeMaxWidth
    );
    setWidth(width);
    handleChangeWidth?.(width);
  };

  const onAnimationEnd = (event: React.AnimationEvent<HTMLDivElement>) => {
    if (event.currentTarget !== event.target) return;
    if (!closing) return;
    onClose();
    setClosing(false);
  };

  if (!isOpen) return null;

  return (
    <div
      className={cn(styles.container, {
        [styles.closing]: closing,
        [styles.fixed]: position === "fixed",
        [styles.sticky]: position === "sticky"
      })}
      ref={wrapperRef}
      data-testid="features-SideHelp"
      style={{ width, minWidth: width, top, height: `calc(100vh - ${top}px)` }}
      onAnimationEnd={onAnimationEnd}
    >
      {resizable && (
        <div
          className={styles.resizeHandle}
          onPointerDown={onResizePointerDown}
          onPointerMove={onResizePointerMove}
          onPointerUp={onResizePointerUp}
          onPointerCancel={onResizePointerUp}
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize side panel"
        />
      )}
      <div className={styles.panel}>
        <div className={styles.titleWrap}>
          <div className={styles.title}>{title}</div>
          <div
            className={styles.closeWrap}
            onClick={() => {
              setClosing(true);
            }}
            data-testid="features-SideHelp-close"
          >
            <span className={styles.text}>閉じる</span>
            <span className={styles.closeButton} />
          </div>
        </div>
        <div className={styles.content}>{children}</div>
      </div>
    </div>
  );
};
